import { useEffect, useState, useRef } from "react";

interface DropdownOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

interface AnimatedDropdownProps {
  options: DropdownOption[];
  onOptionClick: (option: DropdownOption) => void;
  triggerText?: string;
  triggerClassName?: string;
  disabled?: boolean;
  placeholder?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  showArrow?: boolean;
}

const CustomAnimatedDropdown: React.FC<AnimatedDropdownProps> = ({
  options,
  onOptionClick,
  triggerText = "Select Option",
  triggerClassName = "",
  disabled = false,
  placeholder = "Select an option",
  dropdownClassName = "",
  optionClassName = "",
  showArrow = true,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleOptionClick = (option: DropdownOption) => {
    setSelectedOption(option);
    setShowDropdown(false);
    onOptionClick(option);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setShowDropdown(!showDropdown);
    }
  };

  const defaultTriggerClasses =
    "bg-primary rounded-lg font-openSans font-semibold h-8 md:h-10 md:px-3 px-2 py-0.5 text-[10px] md:text-web-body-sm text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:bg-black flex items-center justify-between";

  const defaultDropdownClasses =
    "absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 transition-all duration-200 ease-in-out transform origin-top";

  const defaultOptionClasses =
    "w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors duration-150 flex items-center space-x-2";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        disabled={disabled}
        onClick={toggleDropdown}
        className={`${defaultTriggerClasses} ${triggerClassName}`}
      >
        <span>{selectedOption ? selectedOption.label : triggerText}</span>
        {showArrow && (
          <svg
            className={`w-4 h-4 ml-2 transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </button>

      <div
        className={`${defaultDropdownClasses} ${dropdownClassName} ${
          showDropdown
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="py-1">
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => handleOptionClick(option)}
              className={`${defaultOptionClasses} ${optionClassName}`}
            >
              {option.icon && <span>{option.icon}</span>}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomAnimatedDropdown;
